import { makeObservable, observable, action, runInAction, toJS } from "mobx";
import api from "../modules/Admin/api";
import { SearchRequest } from "../api/Types";
import Constants from "../constants/Common";
import {
  PerPageSettings,
  Sorter,
  Filter,
  ExpandableKeys,
  StatusInfo,
  WithBooleanFlag,
  Searcher,
} from "./core";

class RoutesStore {
  routesData = [];

  routesParamsRowKeys: ExpandableKeys = new ExpandableKeys();
  isShowAddForm = new WithBooleanFlag(false);
  routesIsLoading = new WithBooleanFlag(false);
  paginator = new PerPageSettings();
  sorter = new Sorter();
  filter = new Filter();
  search = new Searcher([], true);

  routeStatus = new StatusInfo();

  constructor() {
    makeObservable(
      this,
      {
        routesData: observable,
        getRoutesList: action,
      },
      {
        deep: true,
      },
    );
  }

  getRoutesList = async () => {
    this.routesIsLoading.setTrue();
    try {
      const request: SearchRequest = {
        page: this.paginator.page,
        count: this.paginator.perPage,
        sortItems: this.sorter.sortData,
        filterItems: this.filter.filterItems,
        search: this.search.getSearch(),
      };

      const res = await api.getRoutes(request);
      runInAction(() => {
        this.routesData = res.data.items;
        this.paginator.updateMeta(res.data);
      });
    } catch (e) {
      console.log(e);
    } finally {
      this.routesIsLoading.setFalse();
    }
  };

  createRoute = async (values) => {
    this.routeStatus.resetStatus();
    try {
      const res = await api.postRoute(values);
      runInAction(() => {
        this.routeStatus.setStatus(Constants.requestStatuses.created);
        this.getRoutesList();
      });
    } catch (e) {
      this.routeStatus.setStatus(Constants.requestStatuses.error);
    }
  };

  updateRoute = async (values) => {
    this.routeStatus.resetStatus();
    try {
      const res = await api.putRoute(values);
      runInAction(() => {
        this.routeStatus.setStatus(Constants.requestStatuses.updated);
        this.getRoutesList();
      });
    } catch (e) {
      this.routeStatus.setStatus(Constants.requestStatuses.error);
    }
  };

  deleteRoute = async (id) => {
    this.routeStatus.resetStatus();
    try {
      const res = await api.deleteRoute(id);
      runInAction(() => {
        this.routeStatus.setStatus(Constants.requestStatuses.deleted);
        this.getRoutesList();
      });
    } catch (e) {
      this.routeStatus.setStatus(Constants.requestStatuses.error);
    }
  };

  sendEntityToApproval = async (data) => {
    try {
      return await api.sendEntityToApproval(data);
    } catch (e) {
      this.routeStatus.setStatus(Constants.requestStatuses.error);
    }
  };

  startAgreement = async (entityName, entityId) => {
    try {
      return await api.startAgreement(entityName, entityId);
    } catch (e) {
      this.routeStatus.setStatus(Constants.requestStatuses.error);
    }
  };

  getEntityApprovalState = async (entityName, entityId) => {
    try {
      return await api.getEntityApprovalState(entityName, entityId);
    } catch (e) {
      this.routeStatus.setStatus(Constants.requestStatuses.error);
    }
  };

  getEntityApprovalHistory = async (entityName, entityId) => {
    try {
      return await api.getEntityApprovalHistory(entityName, entityId);
    } catch (e) {
      this.routeStatus.setStatus(Constants.requestStatuses.error);
    }
  };

  getEntityStateList = async (entityName) => {
    try {
      const res = await api.getEntityStateList(entityName);
      return res.data;
    } catch (e) {
      this.routeStatus.setStatus(Constants.requestStatuses.error);
    }
  };
}

const routesStore = new RoutesStore();
export default routesStore;
